import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Loader2, ArrowRight, X, ExternalLink, MessageSquarePlus,
  User as UserIcon, RefreshCw, AlertCircle, Plus, Factory,
} from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { adminClient } from '../lib/adminClient';
import { RFQ_STATUS_ORDER, RFQ_STATUS_META, isTerminal } from '../lib/rfqStatus';
import { track, EVENTS } from '../lib/analytics';

/**
 * /admin/rfqs — GIWAHS Operations Center
 *
 * Kanban-style status board with a detail drawer for full timeline,
 * assignment, status advancement, and internal notes. All mutations
 * go through SECURITY DEFINER RPCs that authenticate via is_admin().
 */

const STATUS_COLUMNS = [
  { key: 'submitted',              label: 'New',                short: 'New' },
  { key: 'in_queue',               label: 'In Queue',           short: 'Queue' },
  { key: 'reviewing_requirements', label: 'In Review',          short: 'Review' },
  { key: 'supplier_shortlisting',  label: 'Supplier Matching',  short: 'Match' },
  { key: 'quotes_requested',       label: 'Quotes Out',         short: 'Quotes' },
  { key: 'quotes_received',        label: 'Quotes Received',    short: 'Recvd' },
  { key: 'buyer_review',           label: 'Buyer Review',       short: 'Review' },
  { key: 'closed',                 label: 'Closed',             short: 'Closed' },
];

const fmtDate = (iso) => !iso ? '—' : new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
const fmtShortDate = (iso) => !iso ? '—' : new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });

const Metric = ({ label, value, sublabel }) => (
  <div className="border border-black bg-white p-4">
    <div className="label-overline text-neutral-500">{label}</div>
    <div className="mt-2 font-display text-2xl tracking-tight md:text-3xl" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value ?? '—'}</div>
    {sublabel && <div className="mt-1 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">{sublabel}</div>}
  </div>
);

const Pill = ({ children, tone = 'neutral' }) => {
  const tones = {
    neutral: 'border-neutral-300 bg-neutral-50 text-neutral-700',
    active:  'border-black bg-black text-white',
    success: 'border-emerald-600 bg-emerald-50 text-emerald-900',
    closed:  'border-neutral-400 bg-neutral-100 text-neutral-600',
  };
  return <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider ${tones[tone] || tones.neutral}`}>{children}</span>;
};

const RfqCard = ({ rfq, onOpen }) => {
  const category = (rfq.product_type || '').split(':')[0]?.trim() || rfq.product_type || '—';
  const region = rfq.shipping_destination || rfq.buyer_country || '—';
  return (
    <button
      type="button"
      data-testid={`admin-rfq-card-${rfq.reference_code}`}
      onClick={() => onOpen(rfq)}
      className="block w-full border border-black bg-white p-3 text-left hover:bg-neutral-50"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono-data text-[11px] tracking-wider">{rfq.reference_code}</span>
        <span className="font-mono-data text-[10px] text-neutral-500">{fmtShortDate(rfq.created_at)}</span>
      </div>
      <div className="mt-2 truncate text-sm font-semibold">{rfq.buyer_company || rfq.buyer_name || '—'}</div>
      <div className="mt-1 truncate text-xs text-neutral-600">{category} · {rfq.target_quantity ? `${Number(rfq.target_quantity).toLocaleString()} units` : '— units'}</div>
      <div className="mt-1 truncate text-xs text-neutral-500">{region}</div>
      {rfq.assigned_to && (
        <div className="mt-2 inline-flex items-center gap-1.5 border border-neutral-300 bg-[#F5F5F0] px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-neutral-700">
          <UserIcon size={10} strokeWidth={1.5} /> {rfq.assigned_to}
        </div>
      )}
    </button>
  );
};

const AttachSuppliersControl = ({ reference, busy, onSaved }) => {
  const [pool, setPool] = useState([]);          // [{slug,name,country_slug,tier}]
  const [attached, setAttached] = useState([]);  // string[] of slugs
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminClient.listSuppliersForPicker(),
      adminClient.getRfq(reference),
    ]).then(([list, rfq]) => {
      if (cancelled) return;
      setPool(list);
      // attached_supplier_slugs not in admin_get_rfq output — fall back to []
      const slugs = rfq && Array.isArray(rfq.attached_supplier_slugs) ? rfq.attached_supplier_slugs : [];
      setAttached(slugs);
    }).catch((e) => console.warn('[attach] load failed', e?.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reference]);

  const toggle = (slug) =>
    setAttached((cur) => cur.includes(slug) ? cur.filter(s => s !== slug) : [...cur, slug]);

  const save = async () => {
    if (saving || busy) return;
    setSaving(true);
    try {
      await adminClient.attachSuppliers(reference, attached);
      toast.success(`Attached ${attached.length} supplier(s) to ${reference}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.message || 'Could not attach suppliers.');
    } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(s => s.slug.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q) || s.country_slug?.toLowerCase().includes(q));
  }, [pool, query]);

  return (
    <div data-testid="admin-rfq-attach-control" className="mt-4">
      <div className="flex items-center justify-between">
        <span className="label-overline text-neutral-500">Attached suppliers ({attached.length})</span>
        <button
          onClick={save}
          disabled={saving || loading || busy}
          data-testid="admin-rfq-attach-save"
          className="inline-flex items-center gap-1.5 border border-black bg-black px-3 py-1 font-mono-data text-[10px] uppercase tracking-wider text-white hover:bg-neutral-800 disabled:opacity-50"
        >
          {saving ? <Loader2 size={10} strokeWidth={1.5} className="animate-spin" /> : <Plus size={10} strokeWidth={1.5} />} Save
        </button>
      </div>

      {/* Selected chips */}
      {attached.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="admin-rfq-attached-chips">
          {attached.map((slug) => {
            const s = pool.find(p => p.slug === slug);
            return (
              <span key={slug} className="inline-flex items-center gap-1.5 border border-black bg-[#F5F5F0] px-2 py-0.5 font-mono-data text-[10px]">
                <Factory size={10} strokeWidth={1.5} /> {s?.name || slug}
                <button onClick={() => toggle(slug)} aria-label="Remove" className="ml-1 hover:text-red-700"><X size={10} strokeWidth={1.5} /></button>
              </span>
            );
          })}
        </div>
      )}

      {/* Search + checklist */}
      <div className="mt-3 border border-black">
        <div className="flex items-stretch border-b border-black">
          <Search size={12} strokeWidth={1.5} className="ml-2 self-center text-neutral-500" />
          <input
            data-testid="admin-rfq-attach-search"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, slug, country…"
            className="flex-1 bg-transparent px-2 py-1.5 text-xs outline-none"
          />
        </div>
        <div className="max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-3 text-center"><Loader2 size={14} strokeWidth={1.5} className="mx-auto animate-spin text-neutral-500" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-center text-[10px] uppercase tracking-wider text-neutral-400">no suppliers match</div>
          ) : (
            filtered.map((s) => {
              const checked = attached.includes(s.slug);
              return (
                <label key={s.slug} data-testid={`admin-rfq-attach-option-${s.slug}`}
                       className={`flex cursor-pointer items-center gap-2 border-t border-neutral-200 px-2 py-1.5 text-xs hover:bg-neutral-50 ${checked ? 'bg-emerald-50' : ''}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(s.slug)} className="accent-black" />
                  <span className="flex-1 truncate"><span className="font-semibold">{s.name}</span> <span className="text-neutral-500">· {s.country_slug || '—'}</span></span>
                  <span className="font-mono-data text-[9px] uppercase tracking-wider text-neutral-500">{s.tier}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const DetailDrawer = ({ rfq, onClose, onRefresh }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [assignText, setAssignText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminClient.getRfq(rfq.reference_code);
      setDetail(d);
      setAssignText(d?.assigned_to || '');
      track(EVENTS.RFQ_VIEWED, { reference_code: rfq.reference_code });
    } finally { setLoading(false); }
  }, [rfq.reference_code]);

  useEffect(() => { load(); }, [load]);

  const handleAdvance = async (newStatus) => {
    if (busy) return;
    setBusy(true);
    try {
      await adminClient.advanceStatus(rfq.reference_code, newStatus);
      toast.success(`Status → ${RFQ_STATUS_META[newStatus]?.label || newStatus}`);
      await load(); onRefresh();
    } catch (e) {
      toast.error(e?.message || 'Could not update status.');
    } finally { setBusy(false); }
  };

  const handleAssign = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await adminClient.assignRfq(rfq.reference_code, assignText.trim());
      toast.success(assignText.trim() ? `Assigned to ${assignText.trim()}` : 'Assignment cleared');
      await load(); onRefresh();
    } catch (e) {
      toast.error(e?.message || 'Could not assign.');
    } finally { setBusy(false); }
  };

  const handleAddNote = async () => {
    const txt = noteText.trim();
    if (!txt || busy) return;
    setBusy(true);
    try {
      await adminClient.addNote(rfq.reference_code, txt);
      setNoteText('');
      toast.success('Note added');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not add note.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-rfq-drawer" className="fixed inset-0 z-50 flex">
      <button type="button" aria-label="Close" onClick={onClose} className="flex-1 bg-black/50" />
      <aside className="flex w-full max-w-2xl flex-col overflow-y-auto bg-white">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-black bg-white px-6 py-4">
          <div>
            <div className="label-overline text-neutral-500">Reference</div>
            <h2 data-testid="admin-rfq-drawer-ref" className="font-display text-2xl tracking-tight">{rfq.reference_code}</h2>
          </div>
          <button onClick={onClose} aria-label="Close drawer" className="border border-black p-2 hover:bg-black hover:text-white" data-testid="admin-rfq-drawer-close">
            <X size={14} strokeWidth={1.5} />
          </button>
        </header>

        {loading || !detail ? (
          <div className="flex flex-1 items-center justify-center"><Loader2 size={20} strokeWidth={1.5} className="animate-spin text-neutral-500" /></div>
        ) : (
          <div className="space-y-6 p-6">
            {/* Status + assignment */}
            <div className="flex flex-wrap items-center gap-3">
              <Pill tone={isTerminal(detail.status) ? 'closed' : 'active'}>● {RFQ_STATUS_META[detail.status]?.short || detail.status}</Pill>
              {detail.assigned_to && <Pill><UserIcon size={10} strokeWidth={1.5} /> {detail.assigned_to}</Pill>}
            </div>

            {/* Buyer summary */}
            <div className="border border-black p-4">
              <div className="label-overline">Buyer</div>
              <div className="mt-2 grid grid-cols-2 gap-y-2 text-sm">
                <div><div className="label-overline text-neutral-500">Name</div>{detail.buyer_name || '—'}</div>
                <div><div className="label-overline text-neutral-500">Company</div>{detail.buyer_company || '—'}</div>
                <div className="col-span-2"><div className="label-overline text-neutral-500">Email</div><a className="underline" href={`mailto:${detail.buyer_email}`}>{detail.buyer_email}</a></div>
              </div>
            </div>

            {/* RFQ details */}
            <div className="border border-black p-4">
              <div className="label-overline">Brief</div>
              <div className="mt-2 space-y-2 text-sm">
                <div><div className="label-overline text-neutral-500">Category / Product</div>{detail.product_type || '—'}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><div className="label-overline text-neutral-500">Quantity</div>{detail.target_quantity ? `${Number(detail.target_quantity).toLocaleString()} units` : '—'}</div>
                  <div><div className="label-overline text-neutral-500">Target price</div>{detail.target_unit_price || '—'}</div>
                </div>
                <div><div className="label-overline text-neutral-500">Destination</div>{detail.shipping_destination || '—'}</div>
                {detail.preferred_countries?.length > 0 && <div><div className="label-overline text-neutral-500">Preferred sourcing</div>{detail.preferred_countries.join(', ')}</div>}
                {detail.needed_by_date && <div><div className="label-overline text-neutral-500">Needed by</div>{detail.needed_by_date}</div>}
                {detail.notes && <div><div className="label-overline text-neutral-500">Notes</div><div className="whitespace-pre-wrap text-neutral-700">{detail.notes}</div></div>}
              </div>
            </div>

            {/* Actions */}
            <div className="border border-black p-4">
              <div className="label-overline">Actions</div>

              {/* Assign */}
              <div className="mt-3">
                <label className="label-overline text-neutral-500">Assign to</label>
                <div className="mt-1 flex items-stretch border border-black">
                  <input
                    data-testid="admin-rfq-assign-input"
                    value={assignText} onChange={(e) => setAssignText(e.target.value)}
                    placeholder="ops@giwahs.com"
                    className="flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-neutral-400"
                  />
                  <button data-testid="admin-rfq-assign-btn" onClick={handleAssign} disabled={busy}
                    className="border-l border-black bg-black px-4 text-[10px] uppercase tracking-wider text-white hover:bg-neutral-800 disabled:opacity-50">
                    Save
                  </button>
                </div>
              </div>

              {/* Advance */}
              <div className="mt-4">
                <div className="label-overline text-neutral-500">Advance status</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {RFQ_STATUS_ORDER.filter(s => s !== detail.status).map(s => (
                    <button
                      key={s}
                      data-testid={`admin-rfq-advance-${s}`}
                      onClick={() => handleAdvance(s)} disabled={busy}
                      className="inline-flex items-center gap-1 border border-black px-2 py-1 font-mono-data text-[10px] uppercase tracking-wider hover:bg-black hover:text-white disabled:opacity-50"
                    >
                      {RFQ_STATUS_META[s]?.short || s} <ArrowRight size={10} strokeWidth={1.5} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div className="mt-4">
                <label className="label-overline text-neutral-500">Add internal note (appears on buyer timeline)</label>
                <div className="mt-1 flex items-stretch border border-black">
                  <input
                    data-testid="admin-rfq-note-input"
                    value={noteText} onChange={(e) => setNoteText(e.target.value)}
                    placeholder="e.g. Awaiting MOQ confirmation from supplier #2"
                    className="flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-neutral-400"
                  />
                  <button data-testid="admin-rfq-note-btn" onClick={handleAddNote} disabled={busy || !noteText.trim()}
                    className="inline-flex items-center gap-1.5 border-l border-black bg-black px-3 text-[10px] uppercase tracking-wider text-white hover:bg-neutral-800 disabled:opacity-50">
                    <MessageSquarePlus size={12} strokeWidth={1.5} /> Post
                  </button>
                </div>
              </div>

              {/* Attach suppliers (closes the network-effect loop) */}
              <AttachSuppliersControl reference={detail.reference_code} busy={busy} onSaved={load} />

              {/* External links */}
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to={`/rfq/${detail.reference_code}`} target="_blank" className="inline-flex items-center gap-1.5 border border-black px-3 py-1.5 text-[10px] uppercase tracking-wider hover:bg-black hover:text-white">
                  Buyer dashboard <ExternalLink size={10} strokeWidth={1.5} />
                </Link>
                <Link to={`/track?ref=${encodeURIComponent(detail.reference_code)}`} target="_blank" className="inline-flex items-center gap-1.5 border border-black px-3 py-1.5 text-[10px] uppercase tracking-wider hover:bg-black hover:text-white">
                  Public tracking <ExternalLink size={10} strokeWidth={1.5} />
                </Link>
              </div>
            </div>

            {/* Timeline */}
            <div data-testid="admin-rfq-timeline">
              <div className="label-overline">Audit history</div>
              <ol className="mt-3 border-l border-black">
                {(detail.events_json || []).slice().reverse().map((ev) => (
                  <li key={ev.id} className="relative -ml-px border-l-[3px] border-black pb-4 pl-5">
                    <span className="absolute -left-[6px] top-1 grid h-2.5 w-2.5 place-items-center border border-black bg-black" />
                    <div className="flex flex-wrap items-center gap-2">
                      <Pill tone={ev.actor === 'admin' ? 'active' : ev.actor === 'system' ? 'neutral' : 'success'}>{ev.actor}</Pill>
                      <span className="font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">{RFQ_STATUS_META[ev.status]?.short || ev.status}</span>
                      <span className="font-mono-data text-[10px] text-neutral-400">{fmtDate(ev.created_at)}</span>
                    </div>
                    {ev.note && <p className="mt-1 text-sm text-neutral-700">{ev.note}</p>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

const AdminRfqs = () => {
  const [rfqs, setRfqs] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [openRef, setOpenRef] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setError('');
      const [list, m] = await Promise.all([adminClient.listRfqs(), adminClient.metrics()]);
      setRfqs(list);
      setMetrics(m);
    } catch (e) {
      setError(e?.message || 'Could not load.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rfqs;
    return rfqs.filter(r =>
      r.reference_code?.toLowerCase().includes(q) ||
      r.buyer_company?.toLowerCase().includes(q) ||
      r.buyer_email?.toLowerCase().includes(q) ||
      r.buyer_name?.toLowerCase().includes(q) ||
      r.product_type?.toLowerCase().includes(q)
    );
  }, [rfqs, query]);

  const columns = useMemo(() => {
    const grouped = {};
    for (const col of STATUS_COLUMNS) grouped[col.key] = [];
    for (const r of filtered) {
      const key = grouped[r.status] ? r.status : 'submitted';
      grouped[key].push(r);
    }
    return grouped;
  }, [filtered]);

  const medianHrs = metrics.median_response_hrs;
  const medianLabel = medianHrs == null ? '—' : medianHrs < 1 ? `${Math.round(medianHrs * 60)}m` : `${Number(medianHrs).toFixed(1)}h`;
  const conversionPct = metrics.total ? Math.round(((metrics.total - (metrics.open || 0)) / metrics.total) * 100) : null;
  const topCat = (metrics.top_categories || [])[0];
  const topCountry = (metrics.top_countries || [])[0];

  const openCard = (r) => setOpenRef(r.reference_code);
  const openRfq = useMemo(() => rfqs.find(r => r.reference_code === openRef), [rfqs, openRef]);

  return (
    <div data-testid="admin-rfqs-page" className="bg-[#F5F5F0]">
      <SEO title="Operations Center — Admin" description="GIWAHS sourcing desk operations command center." noindex />

      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-8 md:px-8 md:py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="label-overline text-neutral-500">/ Admin · Operations</span>
              <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,4vw,3.25rem)', lineHeight: 0.95 }}>
                Operations Center.
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-neutral-600">Triage every active RFQ, advance lifecycle states, assign owners, and post notes that surface on the buyer's timeline.</p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/admin/suppliers/claims"
                data-testid="admin-rfqs-link-claims"
                className="hidden md:inline-flex items-center gap-1.5 border border-black bg-white px-3 py-2 text-[10px] uppercase tracking-wider hover:bg-black hover:text-white"
              >
                Supplier claims
              </Link>
              <div className="flex items-stretch border border-black bg-white">
                <Search size={14} strokeWidth={1.5} className="ml-3 self-center text-neutral-500" />
                <input
                  data-testid="admin-rfqs-search"
                  type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search ref, buyer, company…"
                  className="px-3 py-2 text-sm outline-none placeholder:text-neutral-400" style={{ minWidth: 240 }}
                />
              </div>
              <button onClick={refresh} aria-label="Refresh" data-testid="admin-rfqs-refresh" className="border border-black bg-white p-2 hover:bg-black hover:text-white">
                <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] grid grid-cols-2 gap-3 px-5 py-6 md:grid-cols-6 md:px-8">
          <Metric label="New today"        value={metrics.new_today ?? '—'} />
          <Metric label="Open RFQs"        value={metrics.open ?? '—'} sublabel={`of ${metrics.total ?? 0} total`} />
          <Metric label="Closed"           value={metrics.closed ?? '—'} />
          <Metric label="Median response"  value={medianLabel} sublabel="submit → first action" />
          <Metric label="Top category"     value={topCat?.category || '—'} sublabel={topCat ? `${topCat.n} RFQs` : ''} />
          <Metric label="Top country"      value={topCountry?.country || '—'} sublabel={topCountry ? `${topCountry.n} RFQs` : ''} />
        </div>
      </section>

      {/* Error */}
      {error && (
        <div data-testid="admin-rfqs-error" className="border-b border-red-300 bg-red-50 px-5 py-3 text-sm text-red-800 md:px-8">
          <AlertCircle size={14} strokeWidth={1.5} className="mr-2 inline" /> {error}
        </div>
      )}

      {/* Kanban */}
      <section className="bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-6 md:px-8 md:py-8">
          {loading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <Loader2 size={20} strokeWidth={1.5} className="animate-spin text-neutral-500" />
            </div>
          ) : (
            <div data-testid="admin-rfqs-board" className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
              {STATUS_COLUMNS.map(col => (
                <div key={col.key} data-testid={`admin-rfqs-column-${col.key}`} className="flex flex-col">
                  <div className="mb-3 flex items-baseline justify-between">
                    <span className="label-overline">{col.label}</span>
                    <span className="font-mono-data text-[10px] tracking-wider text-neutral-500">{columns[col.key].length}</span>
                  </div>
                  <div className="space-y-2">
                    {columns[col.key].length === 0
                      ? <div className="border border-dashed border-neutral-300 bg-white/40 p-3 text-center text-[10px] uppercase tracking-wider text-neutral-400">empty</div>
                      : columns[col.key].map(r => <RfqCard key={r.reference_code} rfq={r} onOpen={openCard} />)
                    }
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {openRfq && <DetailDrawer rfq={openRfq} onClose={() => setOpenRef(null)} onRefresh={refresh} />}
    </div>
  );
};

export default AdminRfqs;
